/**
 * MobileTransactionsSection Component
 *
 * Mobile-optimized transaction display for the Transactions section
 * of MobileAccountDetail. Self-contained component that owns its own
 * transaction list and loading state — does NOT participate in
 * AccountsContext dirty tracking (transactions save immediately).
 *
 * Displays a compact 3-column AG Grid (Date, Type, Amount) with
 * current balance header, Add Transaction, and row-tap action sheet
 * for View Details / Edit / Delete (conditional on manual vs trade-linked).
 *
 * Includes filter and timezone override controls.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Clock, Filter, Link2, Plus } from 'lucide-react';
import type { ColDef, GridApi } from 'ag-grid-community';

// 2. Workspace packages
import {
  AGGridTable,
  Spinner,
  Button,
  ButtonGroup,
  useDisclosure,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Autocomplete,
  AutocompleteItem,
} from '@enfomentis/ui';
import type { ButtonGroupOption } from '@enfomentis/ui';

// 3. Internal absolute imports
import { TRANSACTION_TYPE_DISPLAY_MAP } from '@/constants';
import { TIMEZONES } from '@/constants/timezone';
import { balanceHistoryService } from '@/services/BalanceHistoryService';
import { basicSettingsService } from '@/services/BasicSettingsService';
import { brokerService } from '@/services/BrokerService';
import type { BalanceHistory } from '@/types/schema/BalanceHistory';
import type { Broker } from '@/types/schema/Broker';
import { fromUTC } from '@/utils/dateHelpers';
import { formatMoney } from '@/utils/moneyHelpers';

// 4. Relative imports
import { useAccounts } from '../context';
import { TransactionActionSheet } from './TransactionActionSheet';
import { TransactionModal } from './TransactionModal';

// ---------------------------------------------------------------------------
// Filter Options
// ---------------------------------------------------------------------------

const TRANSACTION_FILTER_OPTIONS: ButtonGroupOption[] = [
  { value: 'all', label: 'All' },
  { value: 'manual', label: 'Manual Only' },
  { value: 'trade-linked', label: 'Trade-Linked Only' },
];

// ---------------------------------------------------------------------------
// Cell Renderers
// ---------------------------------------------------------------------------

const TypeRenderer: React.FC<{ data: BalanceHistory }> = ({ data }) => {
  const label = TRANSACTION_TYPE_DISPLAY_MAP[data.transactionType];
  const isTradeLinked = !!(data.sourceExecutionEventId || data.tradeExecutionId);

  return (
    <div className="flex items-center gap-1.5">
      <span>{label}</span>
      {isTradeLinked && (
        <Link2 size={14} className="text-text-tertiary shrink-0" />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const MobileTransactionsSection: React.FC = () => {
  const {
    selectedAccountId,
    formState,
    activeBrokers,
  } = useAccounts();

  // Local state — self-contained, not part of AccountsContext
  const [transactions, setTransactions] = useState<BalanceHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [userTimezone, setUserTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Archived broker lookup — fetches broker by ID when not in activeBrokers
  const [linkedBroker, setLinkedBroker] = useState<Broker | null>(null);

  useEffect(() => {
    if (!formState.brokerId) {
      setLinkedBroker(null);
      return;
    }
    const inActive = activeBrokers.some((b) => b.id === formState.brokerId);
    if (inActive) {
      setLinkedBroker(null);
      return;
    }
    let cancelled = false;
    brokerService.get(formState.brokerId).then((broker) => {
      if (!cancelled) {
        setLinkedBroker(broker ?? null);
      }
    }).catch((error) => {
      console.error('Failed to load linked broker for mobile transactions:', error);
      if (!cancelled) {
        setLinkedBroker(null);
      }
    });
    return () => { cancelled = true; };
  }, [formState.brokerId, activeBrokers]);

  // ---------------------------------------------------------------------------
  // Currency resolution: account → broker (active or archived) → USD fallback
  // ---------------------------------------------------------------------------
  const effectiveCurrency = useMemo(() => {
    if (formState.baseCurrency) return formState.baseCurrency;
    const activeBrokerMatch = activeBrokers.find(b => b.id === formState.brokerId);
    const broker = activeBrokerMatch ?? linkedBroker;
    return broker?.feeMetadata?.defaultAccountCurrency || 'USD';
  }, [formState.baseCurrency, formState.brokerId, activeBrokers, linkedBroker]);

  // ---------------------------------------------------------------------------
  // Load user timezone on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    void (async () => {
      try {
        const settings = await basicSettingsService.getOrCreateDefaultSettings();
        if (settings.displayTimezone) {
          setUserTimezone(settings.displayTimezone);
        }
      } catch {
        // Keep browser fallback
      }
    })();
  }, []);

  // ---------------------------------------------------------------------------
  // Load transactions when selected account changes
  // ---------------------------------------------------------------------------
  const loadTransactions = useCallback(async () => {
    if (!selectedAccountId) {
      setTransactions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await balanceHistoryService.getByAccountId(selectedAccountId);
      // Service returns ascending — reverse for most-recent-first display
      setTransactions(data.reverse());
    } catch (error) {
      console.error('Failed to load transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    void loadTransactions();
  }, [loadTransactions]);

  // ---------------------------------------------------------------------------
  // Filter state — same logic as desktop TransactionsTabContent
  // ---------------------------------------------------------------------------
  const [transactionFilter, setTransactionFilter] = useState<string>('all');

  const handleFilterChange = useCallback(
    (value: string | number | (string | number)[] | null): void => {
      if (value != null && typeof value === 'string') {
        setTransactionFilter(value);
      }
    },
    [],
  );

  const filteredTransactions = useMemo(() => {
    if (transactionFilter === 'all') return transactions;
    if (transactionFilter === 'manual') {
      return transactions.filter(
        (tx) => !tx.sourceExecutionEventId && !tx.tradeExecutionId,
      );
    }
    // 'trade-linked'
    return transactions.filter(
      (tx) => !!(tx.sourceExecutionEventId || tx.tradeExecutionId),
    );
  }, [transactions, transactionFilter]);

  // ---------------------------------------------------------------------------
  // Timezone override — display-only, not persisted to BasicSettings
  // ---------------------------------------------------------------------------
  const handleTimezoneChange = useCallback((key: React.Key | null): void => {
    if (key) {
      setUserTimezone(String(key));
    }
  }, []);

  const timezoneButtonLabel = useMemo(() => {
    const match = TIMEZONES.find((tz) => tz.value === userTimezone);
    if (!match) return userTimezone;
    // Extract UTC offset from label format "(UTC-4) New York" → "UTC-4"
    const offsetMatch = match.label.match(/^\(([^)]+)\)/);
    return offsetMatch ? offsetMatch[1] : userTimezone;
  }, [userTimezone]);

  // ---------------------------------------------------------------------------
  // Transaction modal state — add and edit modes
  // ---------------------------------------------------------------------------
  const [transactionModalMode, setTransactionModalMode] = useState<'add' | 'edit' | null>(null);
  const [editingTransaction, setEditingTransaction] = useState<BalanceHistory | null>(null);

  const handleTransactionModalClose = useCallback((): void => {
    setTransactionModalMode(null);
    setEditingTransaction(null);
  }, []);

  const handleTransactionModalSaved = useCallback(async (): Promise<void> => {
    await loadTransactions();
    setTransactionModalMode(null);
    setEditingTransaction(null);
  }, [loadTransactions]);

  // ---------------------------------------------------------------------------
  // Action sheet state — row selection → action sheet
  // ---------------------------------------------------------------------------
  const [actionSheetTransaction, setActionSheetTransaction] = useState<BalanceHistory | null>(null);
  const { isOpen: isActionSheetOpen, onOpen: onActionSheetOpen, onClose: onActionSheetClose } = useDisclosure();
  const gridApiRef = useRef<GridApi | null>(null);
  const previousSelectionCountRef = useRef(0);

  const handleActionSheetClose = useCallback((): void => {
    onActionSheetClose();
    gridApiRef.current?.deselectAll();
    // Note: actionSheetTransaction is NOT cleared here — the drawer needs
    // the payload alive during its close animation. TransactionActionSheet
    // returns null when transaction is null, which would unmount the drawer
    // abruptly instead of animating closed via isOpen=false. The payload
    // is naturally replaced on the next row selection.
  }, [onActionSheetClose]);

  // ---------------------------------------------------------------------------
  // View details state — stable payload decoupled from grid selection
  // ---------------------------------------------------------------------------
  const [viewingTransaction, setViewingTransaction] = useState<BalanceHistory | null>(null);
  const { isOpen: isViewDetailsOpen, onOpen: onViewDetailsOpen, onClose: onViewDetailsClose } = useDisclosure();

  const handleViewDetailsClose = useCallback((): void => {
    onViewDetailsClose();
    setViewingTransaction(null);
  }, [onViewDetailsClose]);

  const handleViewDetails = useCallback((tx: BalanceHistory): void => {
    handleActionSheetClose();
    setViewingTransaction(tx);
    onViewDetailsOpen();
  }, [handleActionSheetClose, onViewDetailsOpen]);

  // ---------------------------------------------------------------------------
  // Edit handler — action sheet → edit modal
  // ---------------------------------------------------------------------------
  const handleEditClick = useCallback((tx: BalanceHistory): void => {
    handleActionSheetClose();
    setEditingTransaction(tx);
    setTransactionModalMode('edit');
  }, [handleActionSheetClose]);

  // ---------------------------------------------------------------------------
  // Delete confirmation state
  // ---------------------------------------------------------------------------
  const [deletingTransaction, setDeletingTransaction] = useState<BalanceHistory | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteClick = useCallback((tx: BalanceHistory): void => {
    handleActionSheetClose();
    setDeletingTransaction(tx);
    setDeleteError(null);
  }, [handleActionSheetClose]);

  const handleDeleteCancel = useCallback((): void => {
    setDeletingTransaction(null);
    setDeleteError(null);
  }, []);

  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!deletingTransaction) return;

    setIsDeleting(true);
    try {
      await balanceHistoryService.softDelete(deletingTransaction.id);
      await loadTransactions();
      setDeletingTransaction(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete transaction';
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [deletingTransaction, loadTransactions]);

  // ---------------------------------------------------------------------------
  // Column definitions (3 mobile columns)
  // ---------------------------------------------------------------------------
  const mobileColumnDefs = useMemo<ColDef<BalanceHistory>[]>(() => [
    {
      headerName: 'Date',
      field: 'timestamp',
      width: 90,
      valueFormatter: (params) => {
        if (!params.value) return '—';
        const d = new Date(params.value);
        if (!Number.isFinite(d.getTime())) return '—';
        return new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: '2-digit',
          timeZone: userTimezone,
        }).format(d);
      },
    },
    {
      headerName: 'Type',
      field: 'transactionType',
      flex: 1,
      cellRenderer: (params: { data?: BalanceHistory }) =>
        params.data ? <TypeRenderer data={params.data} /> : null,
    },
    {
      headerName: 'Amount',
      field: 'amount',
      width: 100,
      type: 'numericColumn',
      valueFormatter: (params) => {
        if (params.value == null || !params.data) return '—';
        return formatMoney(params.value, effectiveCurrency, params.data.amountPrecision);
      },
      cellClassRules: {
        'text-success': (params) => params.value > 0,
        'text-error': (params) => params.value < 0,
      },
    },
  ], [effectiveCurrency, userTimezone]);

  // ---------------------------------------------------------------------------
  // Grid options (mobile-optimized, single-row selection → action sheet)
  // ---------------------------------------------------------------------------
  const gridOptions = useMemo(() => ({
    defaultColDef: {
      resizable: false,
      suppressMovable: true,
      sortable: true,
      filter: false,
    },
    rowHeight: 48,
    animateRows: true,
    suppressMenuHide: true,
    rowSelection: {
      mode: 'singleRow' as const,
      enableClickSelection: true,
      checkboxes: false,
    },
    onGridReady: (params: { api: GridApi }) => {
      gridApiRef.current = params.api;
    },
    onSelectionChanged: (event: { api: GridApi }) => {
      // Guard: no-op if view details drawer is open to prevent re-entry
      if (isViewDetailsOpen) return;

      const selectedRows = event.api.getSelectedRows() as BalanceHistory[];
      const newCount = selectedRows.length;
      const previousCount = previousSelectionCountRef.current;
      previousSelectionCountRef.current = newCount;

      if (newCount === 1 && previousCount !== 1) {
        setActionSheetTransaction(selectedRows[0] ?? null);
        onActionSheetOpen();
      } else if (newCount === 0 && previousCount > 0) {
        onActionSheetClose();
      }
    },
  }), [isViewDetailsOpen, onActionSheetOpen, onActionSheetClose]);

  // ---------------------------------------------------------------------------
  // Current balance from most recent transaction
  // ---------------------------------------------------------------------------
  const latestTransaction = transactions[0] as BalanceHistory | undefined;
  const currentBalance = latestTransaction
    ? formatMoney(
        latestTransaction.balanceAfter,
        effectiveCurrency,
        latestTransaction.balancePrecision
      )
    : null;

  // ---------------------------------------------------------------------------
  // Add Transaction button
  // ---------------------------------------------------------------------------
  const addButton = (
    <Button
      size="sm"
      color="primary"
      variant="flat"
      startContent={<Plus size={16} />}
      onPress={() => setTransactionModalMode('add')}
      isDisabled={!selectedAccountId}
    >
      Add Transaction
    </Button>
  );

  // ---------------------------------------------------------------------------
  // Render — computed content + action sheet + view details + delete modal
  // ---------------------------------------------------------------------------

  let content: React.ReactNode;

  if (loading) {
    content = (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" label="Loading transactions..." />
      </div>
    );
  } else if (!selectedAccountId) {
    content = (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-text-secondary">No account selected</p>
      </div>
    );
  } else if (transactions.length === 0) {
    content = (
      <div className="flex items-center justify-center py-12 text-center">
        <div>
          <p className="text-text-secondary mb-2">
            No transactions found for this account
          </p>
          <p className="text-sm text-text-tertiary mb-4">
            Add a transaction to start tracking your account balance
          </p>
          {addButton}
        </div>
      </div>
    );
  } else {
    content = (
      <>
        {/* Balance header */}
        <div className="px-1">
          <span className="text-sm text-text-tertiary">Current Balance</span>
          <p className="text-lg font-semibold">{currentBalance}</p>
        </div>

        {/* Actions row: Filter + Timezone (left), Add Transaction (right) */}
        <div className="flex items-center justify-between gap-2 px-1">
          <div className="flex items-center gap-2">
            {/* Filter popover */}
            <Popover placement="bottom-start">
              <PopoverTrigger>
                <Button size="sm" variant="flat" startContent={<Filter size={14} />}>
                  Filter
                  {transactionFilter !== 'all' && (
                    <span className="ml-1 inline-block size-2 rounded-full bg-primary" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-3">
                <ButtonGroup
                  options={TRANSACTION_FILTER_OPTIONS}
                  value={transactionFilter}
                  onChange={handleFilterChange}
                  variant="light"
                  size="sm"
                  selectedColor="primary"
                  unselectedColor="default"
                />
              </PopoverContent>
            </Popover>

            {/* Timezone popover */}
            <Popover placement="bottom-start">
              <PopoverTrigger>
                <Button size="sm" variant="flat" startContent={<Clock size={14} />}>
                  {timezoneButtonLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-3 w-72">
                <Autocomplete
                  label="Timezone"
                  labelPlacement="outside"
                  placeholder="Search timezones..."
                  size="sm"
                  variant="bordered"
                  selectedKey={userTimezone}
                  menuTrigger="input"
                  onSelectionChange={handleTimezoneChange}
                  aria-label="Display timezone override"
                  className="max-md:[&_input]:text-base!"
                >
                  {TIMEZONES.map((tz) => (
                    <AutocompleteItem key={tz.value}>
                      {tz.label}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              </PopoverContent>
            </Popover>
          </div>

          {addButton}
        </div>

        {/* Transactions grid or filtered-empty state */}
        {filteredTransactions.length > 0 ? (
          <div className="min-h-0">
            <AGGridTable<BalanceHistory>
              rowData={filteredTransactions}
              columnDefs={mobileColumnDefs}
              height="100%"
              gridOptions={gridOptions}
            />
          </div>
        ) : (
          <div className="h-full min-h-0 flex items-center justify-center text-center">
            <p className="text-text-secondary">
              No {transactionFilter === 'manual' ? 'manual' : 'trade-linked'} transactions found
            </p>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="grid grid-rows-[auto_auto_minmax(0,1fr)] gap-3 flex-1 min-h-0 pb-1">
        {content}
      </div>

      {/* Transaction add/edit modal */}
      {selectedAccountId && (
        <TransactionModal
          isOpen={transactionModalMode !== null}
          onClose={handleTransactionModalClose}
          onSaved={handleTransactionModalSaved}
          accountId={selectedAccountId}
          effectiveCurrency={effectiveCurrency}
          userTimezone={userTimezone}
          {...(editingTransaction ? { editTransaction: editingTransaction } : {})}
        />
      )}

      {/* Action sheet — opened on row tap */}
      <TransactionActionSheet
        isOpen={isActionSheetOpen}
        onClose={handleActionSheetClose}
        transaction={actionSheetTransaction}
        onViewDetails={handleViewDetails}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      />

      {/* View details drawer */}
      <Drawer
        isOpen={isViewDetailsOpen}
        onOpenChange={(open) => {
          if (!open) handleViewDetailsClose();
        }}
        placement="bottom"
      >
        <DrawerContent>
          {() => (
            <>
              <DrawerHeader className="flex flex-col gap-1">
                <h2 className="text-lg font-semibold">Transaction Details</h2>
                {viewingTransaction && (
                  <p className="text-small text-foreground-500">
                    {TRANSACTION_TYPE_DISPLAY_MAP[viewingTransaction.transactionType]}
                    {!!(viewingTransaction.sourceExecutionEventId || viewingTransaction.tradeExecutionId) && ' (Trade-linked)'}
                  </p>
                )}
              </DrawerHeader>
              <DrawerBody>
                {viewingTransaction && (
                  <div className="flex flex-col gap-4">
                    <div>
                      <p className="text-sm text-text-tertiary">Date/Time</p>
                      <p className="text-sm">{fromUTC(viewingTransaction.timestamp, userTimezone, 'display')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Type</p>
                      <p className="text-sm">{TRANSACTION_TYPE_DISPLAY_MAP[viewingTransaction.transactionType]}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Description</p>
                      <p className="text-sm">{viewingTransaction.description || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Amount</p>
                      <p className={`text-sm ${viewingTransaction.amount > 0 ? 'text-success' : viewingTransaction.amount < 0 ? 'text-error' : ''}`}>
                        {formatMoney(viewingTransaction.amount, effectiveCurrency, viewingTransaction.amountPrecision)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Balance After</p>
                      <p className="text-sm">
                        {formatMoney(viewingTransaction.balanceAfter, effectiveCurrency, viewingTransaction.balancePrecision)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">Category</p>
                      <p className="text-sm">{viewingTransaction.category || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-text-tertiary">External Reference</p>
                      <p className="text-sm">{viewingTransaction.externalReference || '—'}</p>
                    </div>
                  </div>
                )}
              </DrawerBody>
            </>
          )}
        </DrawerContent>
      </Drawer>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={!!deletingTransaction}
        onClose={handleDeleteCancel}
        size="sm"
        placement="top-center"
        isDismissable={!isDeleting}
      >
        <ModalContent>
          <ModalHeader>Delete Transaction?</ModalHeader>
          <ModalBody>
            {deletingTransaction && (
              <p className="text-text-secondary">
                Delete this{' '}
                {TRANSACTION_TYPE_DISPLAY_MAP[deletingTransaction.transactionType]}{' '}
                transaction for{' '}
                {formatMoney(
                  deletingTransaction.amount,
                  effectiveCurrency,
                  deletingTransaction.amountPrecision
                )}
                ? This action cannot be undone.
              </p>
            )}
            {deleteError && (
              <Alert color="danger" variant="flat">
                {deleteError}
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="flat"
              onPress={handleDeleteCancel}
              isDisabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={handleDeleteConfirm}
              isLoading={isDeleting}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
